import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import FingoRadio from '@fingo/lib/components/radios/FingoRadio';
import FingoCheckedRadio from '@fingo/lib/components/radios/FingoCheckedRadio';
import CURRENCY_FLAGS from '@fingo/lib/constants/currency-flags';
import InformationStack from '@fingo/lib/components/stacks/InformationStack';
import DEFAULT_RADIO_CLASS_NAME from '@fingo/lib/theme/constants/radio';
import { SUCCESS_STACK_CLASS_NAME } from '@fingo/lib/theme/constants/stack';

const UploadInvoicesMethodOption = ({
  value,
  label,
  description,
  recommended,
  Icon,
  iconSrc,
  currencies,
  onSelect,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={() => onSelect(value)}
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'stretch',
        width: 476,
        maxWidth: '100%',
        minHeight: 151,
        py: 4,
        pr: 3,
        bgcolor: 'common.white',
        boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.18)',
        borderRadius: '12px',
        cursor: 'pointer',
      }}
    >
      <Chip
        label={(
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {currencies.map((code, index) => {
              const FlagIcon = CURRENCY_FLAGS[code];
              return (
                <Stack key={code} direction="row" alignItems="center" spacing={0.5}>
                  {index > 0 && (
                  <Typography variant="small_2" color="common.black">/</Typography>
                  )}
                  {FlagIcon && <FlagIcon />}
                  <Typography variant="small_2" color="common.black">{code}</Typography>
                </Stack>
              );
            })}
          </Stack>
      )}
        size="small"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          bgcolor: 'gray.A200',
          borderRadius: '24px 0px 12px 0px',
          height: 28,
          px: 1.5,
          '& .MuiChip-label': { px: 0 },
        }}
      />
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ flex: 1 }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            width: 74,
            alignSelf: 'stretch',
            flexShrink: 0,
            bgcolor: 'common.white',
            borderRight: '1px solid',
            borderColor: 'gray.A200',
          }}
        >
          <Radio
            className={DEFAULT_RADIO_CLASS_NAME}
            value={value}
            icon={<FingoRadio size={20} />}
            checkedIcon={<FingoCheckedRadio size={20} />}
            sx={{ p: 0 }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0}>
          {iconSrc ? (
            <Box sx={{
              width: 62, flexShrink: 0, display: 'flex', justifyContent: 'flex-start',
            }}
            >
              <Box component="img" src={iconSrc} alt={label} sx={{ height: 28, width: 'auto' }} />
            </Box>
          ) : (
            <Box sx={{ pr: 3, flexShrink: 0, display: 'flex' }}>
              <Icon sx={{
                color: alpha(theme.palette.text.highlight, 0.6), height: 39, width: 39,
              }}
              />
            </Box>
          )}
          <Stack sx={{ width: 285 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography
                variant="small_3"
                fontWeight={600}
                color="gray.A600"
                sx={{ lineHeight: '24px' }}
              >
                {label}
              </Typography>
              {recommended && (
                <InformationStack
                  text="Recomendado"
                  className={SUCCESS_STACK_CLASS_NAME}
                  variant="small_2"
                  fontWeight={500}
                  sx={{ borderRadius: 16 }}
                />
              )}
            </Stack>
            <Typography
              variant="small_2"
              fontWeight={500}
              color="gray.A600"
              sx={{ lineHeight: '20px' }}
            >
              {description}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

UploadInvoicesMethodOption.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  recommended: PropTypes.bool,
  Icon: PropTypes.elementType,
  iconSrc: PropTypes.string,
  currencies: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
};

UploadInvoicesMethodOption.defaultProps = {
  recommended: false,
  Icon: null,
  iconSrc: null,
};

export default UploadInvoicesMethodOption;
